import { v4 as uuidv4 } from 'uuid';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { createInlinePdfObjectUrl, revokeInlinePdfObjectUrl } from '../../utils/storagePdfViewer';
import { authClient } from '../../utils/amplifyClient';
import { S3_RESOURCE_LIBRARY_PATH } from '../../utils/constants';
import type { User } from '../../types/schema';

export type BriefingMediaType = 'document' | 'video';
export type BriefingStyle = 'Video' | 'Poster' | 'PDF';

export interface AssignedSafetyLessonEntry {
    id?: string | null;
    type?: string | null;
    companyId?: string | null;
    date?: string | null;
}

/** Gen2 stores assignedSafetyLesson as AWSJSON (string in GraphQL variables). */
const updateDocumentAssignmentMutation = /* GraphQL */ `
  mutation UpdateDocument($input: UpdateDocumentInput!) {
    updateDocument(input: $input) {
      id
      title
      assignedSafetyLesson
    }
  }
`;

const updateVideoAssignmentMutation = /* GraphQL */ `
  mutation UpdateVideo($input: UpdateVideoInput!) {
    updateVideo(input: $input) {
      id
      title
      assignedSafetyLesson
    }
  }
`;

export interface BriefingTopic {
    id: string;
    title: string;
    type: BriefingMediaType;
    style: BriefingStyle;
    contentPath: string;
    folderName?: string | null;
    assignedSafetyLesson: AssignedSafetyLessonEntry[];
    lastUsedDate: string | null;
    timesUsed: number;
    displayId: string;
}

export interface TodayBriefingTopic extends BriefingTopic {
    url: string | null;
}

export interface ConfirmationRow {
    userId: string;
    name: string;
    confirmed: boolean;
    dateCompleted?: string | null;
}

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp'];

export function getTodayDateKey(date = new Date()): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function formatBriefingDate(date = new Date()): string {
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export function formatShortDate(isoOrKey: string | null | undefined): string {
    if (!isoOrKey) return '—';
    const datePart = isoOrKey.split('T')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return '—';
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export function parseAssignedLessons(raw: unknown): AssignedSafetyLessonEntry[] {
    if (!raw) return [];
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } catch {
            return [];
        }
    }
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

export function parseSafetyLessons(raw: unknown): Array<{
    id?: string;
    type?: string;
    name?: string;
    dateCompleted?: string;
    signature?: string;
}> {
    if (!raw) return [];
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } catch {
            return [];
        }
    }
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

function isImagePath(contentPath: string): boolean {
    const lower = contentPath.toLowerCase();
    return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export function resolveBriefingStyle(type: BriefingMediaType, contentPath: string): BriefingStyle {
    if (type === 'video') return 'Video';
    if (isImagePath(contentPath)) return 'Poster';
    return 'PDF';
}

function companyAssignments(
    assignments: AssignedSafetyLessonEntry[],
    companyId: string,
): AssignedSafetyLessonEntry[] {
    return assignments.filter((a) => a?.companyId === companyId && a?.date);
}

function assignmentDateKey(assignment: AssignedSafetyLessonEntry): string | null {
    if (!assignment.date) return null;
    return assignment.date.split('T')[0];
}

function buildDisplayId(type: BriefingMediaType, id: string): string {
    const prefix = type === 'video' ? 'V' : 'D';
    return `${prefix}-${id.slice(0, 4).toUpperCase()}`;
}

function toBriefingTopic(
    item: {
        id: string;
        title: string;
        contentPath: string;
        folderName?: string | null;
        assignedSafetyLesson?: unknown;
    },
    type: BriefingMediaType,
    companyId: string,
): BriefingTopic {
    const assignments = parseAssignedLessons(item.assignedSafetyLesson);
    const companyOnes = companyAssignments(assignments, companyId);
    const sortedKeys = companyOnes
        .map(assignmentDateKey)
        .filter((k): k is string => Boolean(k))
        .sort((a, b) => b.localeCompare(a));

    return {
        id: item.id,
        title: item.title,
        type,
        style: resolveBriefingStyle(type, item.contentPath),
        contentPath: item.contentPath,
        folderName: item.folderName,
        assignedSafetyLesson: assignments,
        lastUsedDate: sortedKeys[0] ?? null,
        timesUsed: companyOnes.length,
        displayId: buildDisplayId(type, item.id),
    };
}

function isSharedWithCompany(
    sharedWithCompanyIds: (string | null)[] | null | undefined,
    companyId: string,
): boolean {
    return Array.isArray(sharedWithCompanyIds) && sharedWithCompanyIds.includes(companyId);
}

type ModelListResult<T> = {
    data?: Array<T | null> | null;
    nextToken?: string | null;
    errors?: Array<{ message?: string }>;
};

async function listAllModelItems<T>(
    listPage: (nextToken?: string | null) => Promise<ModelListResult<T>>,
): Promise<T[]> {
    const all: T[] = [];
    let nextToken: string | null | undefined = undefined;

    do {
        const result = await listPage(nextToken);
        if (result.errors?.length) {
            throw new Error(
                result.errors.map((e) => e.message).filter(Boolean).join('; ') || 'Failed to list resources',
            );
        }
        for (const item of result.data ?? []) {
            if (item) all.push(item);
        }
        nextToken = result.nextToken;
    } while (nextToken);

    return all;
}

/** Server-side list filter for Daily Briefing library topics (safetyLesson + company share). */
function briefingLibraryListFilter(companyId: string) {
    return {
        safetyLesson: { eq: true },
        sharedWithCompanyIds: { contains: companyId },
    } as const;
}

/** Server-side list filter for Resources eligible to import into Daily Briefing. */
function importableResourceListFilter(companyId: string) {
    return {
        and: [
            { sharedWithCompanyIds: { contains: companyId } },
            { safetyLesson: { ne: true } },
        ],
    };
}

/**
 * List Daily Briefing topics for a company.
 * Uses AppSync list filters to avoid downloading every Document/Video.
 * (A dedicated GSI on safetyLesson + sharedWithCompanyIds would be the longer-term win.)
 */
export async function listBriefingTopics(companyId: string): Promise<BriefingTopic[]> {
    const filter = briefingLibraryListFilter(companyId);
    const [documents, videos] = await Promise.all([
        listAllModelItems((nextToken) => authClient.models.Document.list({ filter, nextToken })),
        listAllModelItems((nextToken) => authClient.models.Video.list({ filter, nextToken })),
    ]);

    const topics: BriefingTopic[] = [];

    for (const doc of documents) {
        if (!doc?.id || !doc.title || !doc.contentPath) continue;
        // Defense in depth if a filter is ignored by the API.
        if (!doc.safetyLesson) continue;
        if (!isSharedWithCompany(doc.sharedWithCompanyIds, companyId)) continue;
        topics.push(toBriefingTopic(doc, 'document', companyId));
    }

    for (const video of videos) {
        if (!video?.id || !video.title || !video.contentPath) continue;
        if (!video.safetyLesson) continue;
        if (!isSharedWithCompany(video.sharedWithCompanyIds, companyId)) continue;
        topics.push(toBriefingTopic(video, 'video', companyId));
    }

    return topics.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}

export async function listImportableResources(companyId: string): Promise<BriefingTopic[]> {
    const filter = importableResourceListFilter(companyId);
    const [documents, videos] = await Promise.all([
        listAllModelItems((nextToken) => authClient.models.Document.list({ filter, nextToken })),
        listAllModelItems((nextToken) => authClient.models.Video.list({ filter, nextToken })),
    ]);

    const topics: BriefingTopic[] = [];

    for (const doc of documents) {
        if (!doc?.id || !doc.title || !doc.contentPath) continue;
        if (doc.safetyLesson) continue;
        if (!isSharedWithCompany(doc.sharedWithCompanyIds, companyId)) continue;
        topics.push(toBriefingTopic(doc, 'document', companyId));
    }

    for (const video of videos) {
        if (!video?.id || !video.title || !video.contentPath) continue;
        if (video.safetyLesson) continue;
        if (!isSharedWithCompany(video.sharedWithCompanyIds, companyId)) continue;
        topics.push(toBriefingTopic(video, 'video', companyId));
    }

    return topics.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}

export function findTodaysTopic(topics: BriefingTopic[], companyId: string, todayKey = getTodayDateKey()): BriefingTopic | null {
    return (
        topics.find((topic) =>
            companyAssignments(topic.assignedSafetyLesson, companyId).some(
                (a) => assignmentDateKey(a) === todayKey,
            ),
        ) ?? null
    );
}

export async function resolveTopicUrl(
    contentPath: string,
    options?: { expiresIn?: number },
): Promise<string | null> {
    try {
        const fileUrl = await getUrl({
            path: contentPath,
            options: {
                // Long training videos need more than the default signed-URL window.
                expiresIn: options?.expiresIn ?? 60 * 60,
            },
        });
        return fileUrl.url.href;
    } catch {
        return null;
    }
}

export function isPdfBriefingTopic(topic: Pick<BriefingTopic, 'style' | 'contentPath' | 'type'>): boolean {
    if (topic.type === 'video') return false;
    return topic.style === 'PDF' || topic.contentPath.toLowerCase().endsWith('.pdf');
}

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.m4v', '.avi'] as const;

export function isVideoBriefingTopic(topic: Pick<BriefingTopic, 'style' | 'contentPath' | 'type'>): boolean {
    if (topic.type === 'video' || topic.style === 'Video') return true;
    const lower = topic.contentPath.toLowerCase();
    return VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** Blob URL for iframe/object inline PDF viewing (via Amplify downloadData). */
export async function resolveBriefingPdfInlineUrl(contentPath: string): Promise<string | null> {
    return createInlinePdfObjectUrl(contentPath);
}

export function revokeBriefingPdfInlineUrl(objectUrl: string | null | undefined): void {
    revokeInlinePdfObjectUrl(objectUrl);
}

export function getConfirmationStats(
    users: User[],
    topicId: string | null,
    todayKey = getTodayDateKey(),
): { confirmedCount: number; crewCount: number; rows: ConfirmationRow[] } {
    const crew = users.filter((u) => u && !u.archived);
    const rows: ConfirmationRow[] = crew.map((user) => {
        const lessons = parseSafetyLessons(user.safetyLessons);
        const match = lessons.find((lesson) => {
            if (!lesson?.dateCompleted) return false;
            const completedKey = new Date(lesson.dateCompleted);
            if (Number.isNaN(completedKey.getTime())) return false;
            const key = getTodayDateKey(completedKey);
            if (key !== todayKey) return false;
            if (topicId && lesson.id && lesson.id !== topicId) return false;
            return true;
        });
        return {
            userId: user.id,
            name: user.name || user.email || 'Unknown user',
            confirmed: Boolean(match),
            dateCompleted: match?.dateCompleted ?? null,
        };
    });

    return {
        confirmedCount: rows.filter((r) => r.confirmed).length,
        crewCount: rows.length,
        rows: rows.sort((a, b) => {
            if (a.confirmed !== b.confirmed) return a.confirmed ? -1 : 1;
            return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }),
    };
}

/** Persist a Daily Briefing view confirmation on the User.safetyLessons JSON field. */
export async function markBriefingTopicViewed(params: {
    userId: string;
    topic: BriefingTopic;
    existingSafetyLessons: unknown;
    todayKey?: string;
}): Promise<{ alreadyViewed: boolean; nextLessons: ReturnType<typeof parseSafetyLessons> }> {
    const todayKey = params.todayKey ?? getTodayDateKey();
    const existing = parseSafetyLessons(params.existingSafetyLessons).map((lesson) => ({
        id: lesson?.id ?? undefined,
        type: lesson?.type ?? undefined,
        name: lesson?.name ?? undefined,
        dateCompleted: lesson?.dateCompleted ?? undefined,
        signature: lesson?.signature ?? undefined,
    }));

    const alreadyViewed = existing.some((lesson) => {
        if (!lesson?.dateCompleted || !lesson.id || lesson.id !== params.topic.id) return false;
        const completed = new Date(lesson.dateCompleted);
        if (Number.isNaN(completed.getTime())) return false;
        return getTodayDateKey(completed) === todayKey;
    });
    if (alreadyViewed) return { alreadyViewed: true, nextLessons: existing };

    const nextLessons = [
        ...existing,
        {
            id: params.topic.id,
            type: params.topic.type,
            name: params.topic.title,
            dateCompleted: new Date().toLocaleString('en-US', {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
            signature: undefined,
        },
    ];

    // Gen2 stores safetyLessons as AWSJSON — pass a JSON string in the GraphQL variable.
    await authClient.graphql({
        query: /* GraphQL */ `
            mutation UpdateUser($input: UpdateUserInput!) {
                updateUser(input: $input) {
                    id
                    safetyLessons
                }
            }
        `,
        variables: {
            input: {
                id: params.userId,
                safetyLessons: JSON.stringify(nextLessons),
            },
        },
    });

    return { alreadyViewed: false, nextLessons };
}

function stripCompanyDateAssignment(
    assignments: AssignedSafetyLessonEntry[],
    companyId: string,
    dateKey: string,
): AssignedSafetyLessonEntry[] {
    return assignments.filter((a) => {
        if (a?.companyId !== companyId) return true;
        return assignmentDateKey(a) !== dateKey;
    });
}

function parseLocalDateKey(dateKey: string): Date {
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function toIsoLocalMidnight(date = new Date()): string {
    const local = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    return local.toISOString();
}

function toIsoLocalMidnightForDateKey(dateKey: string): string {
    return toIsoLocalMidnight(parseLocalDateKey(dateKey));
}

/** One scheduled topic per company day — used by the scheduler UI + save. */
export type ScheduleDraftEntry = {
    dateKey: string;
    topicId: string;
    topicType: BriefingMediaType;
    title: string;
    style: BriefingStyle;
};

/** Build a dateKey → topic map from current library assignments for a company. */
export function buildCompanyScheduleDraft(
    topics: BriefingTopic[],
    companyId: string,
): Record<string, ScheduleDraftEntry> {
    const draft: Record<string, ScheduleDraftEntry> = {};
    for (const topic of topics) {
        for (const assignment of companyAssignments(topic.assignedSafetyLesson, companyId)) {
            const dateKey = assignmentDateKey(assignment);
            if (!dateKey || draft[dateKey]) continue;
            draft[dateKey] = {
                dateKey,
                topicId: topic.id,
                topicType: topic.type,
                title: topic.title,
                style: topic.style,
            };
        }
    }
    return draft;
}

export async function setTopicForDate(params: {
    companyId: string;
    dateKey: string;
    nextTopic: BriefingTopic;
    allTopics: BriefingTopic[];
}): Promise<void> {
    const { companyId, dateKey, nextTopic, allTopics } = params;
    const assignmentDate = toIsoLocalMidnightForDateKey(dateKey);

    // Best-effort clear of other topics assigned on this date (do not abort if one clear fails).
    for (const topic of allTopics) {
        if (topic.id === nextTopic.id) continue;
        const hasDate = companyAssignments(topic.assignedSafetyLesson, companyId).some(
            (a) => assignmentDateKey(a) === dateKey,
        );
        if (!hasDate) continue;
        try {
            const fresh = await fetchFreshAssignments(topic);
            const cleared = stripCompanyDateAssignment(fresh, companyId, dateKey);
            await updateTopicAssignments(topic, cleared);
        } catch (clearError) {
            console.warn('Could not clear date assignment from topic', topic.id, dateKey, clearError);
        }
    }

    const freshNext = await fetchFreshAssignments(nextTopic);
    const withoutDate = stripCompanyDateAssignment(freshNext, companyId, dateKey);
    const nextAssignments = [
        ...withoutDate,
        {
            id: uuidv4(),
            type: nextTopic.type,
            companyId,
            date: assignmentDate,
        },
    ];
    await updateTopicAssignments(nextTopic, nextAssignments);
}

export async function clearTopicForDate(params: {
    companyId: string;
    dateKey: string;
    topic?: BriefingTopic | null;
    allTopics?: BriefingTopic[];
}): Promise<void> {
    const { companyId, dateKey } = params;
    const topicsToClear = params.allTopics?.length
        ? params.allTopics.filter((topic) =>
              companyAssignments(topic.assignedSafetyLesson, companyId).some(
                  (a) => assignmentDateKey(a) === dateKey,
              ),
          )
        : params.topic
          ? [params.topic]
          : [];

    for (const topic of topicsToClear) {
        const fresh = await fetchFreshAssignments(topic);
        const cleared = stripCompanyDateAssignment(fresh, companyId, dateKey);
        await updateTopicAssignments(topic, cleared);
    }
}

/**
 * Persist scheduler draft vs baseline. Past dates (before todayKey) are ignored.
 *
 * Batches by topic (one fetch + one update per affected Document/Video) instead of
 * per-date setTopicForDate/clearTopicForDate fan-out. Continues after individual
 * topic failures, then throws ScheduleDraftApplyError with details.
 */
export class ScheduleDraftApplyError extends Error {
    readonly failures: ScheduleDraftTopicFailure[];

    constructor(failures: ScheduleDraftTopicFailure[]) {
        const labels = failures.map((f) => f.title || f.topicId);
        const preview = labels.slice(0, 5).join(', ');
        const more = labels.length > 5 ? ` (+${labels.length - 5} more)` : '';
        super(
            failures.length === 1
                ? `Failed to update “${preview}”.`
                : `Failed to update ${failures.length} topics: ${preview}${more}.`,
        );
        this.name = 'ScheduleDraftApplyError';
        this.failures = failures;
    }
}

export type ScheduleDraftTopicFailure = {
    topicId: string;
    title?: string;
    message: string;
    dateKeys: string[];
};

/** Soft cap on how many calendar days may change in one Save (UI + API safeguard). */
export const MAX_SCHEDULE_DRAFT_DATE_CHANGES = 60;

function addDateToTopicMap(map: Map<string, Set<string>>, topicId: string, dateKey: string): void {
    let keys = map.get(topicId);
    if (!keys) {
        keys = new Set();
        map.set(topicId, keys);
    }
    keys.add(dateKey);
}

export async function applyScheduleDraft(params: {
    companyId: string;
    draft: Record<string, ScheduleDraftEntry>;
    baseline: Record<string, ScheduleDraftEntry>;
    allTopics: BriefingTopic[];
    todayKey?: string;
}): Promise<void> {
    const todayKey = params.todayKey ?? getTodayDateKey();
    const { companyId, draft, baseline, allTopics } = params;
    const dateKeys = new Set([...Object.keys(baseline), ...Object.keys(draft)]);

    const removalsByTopicId = new Map<string, Set<string>>();
    const additionsByTopicId = new Map<string, Set<string>>();
    let changedDateCount = 0;

    for (const dateKey of [...dateKeys].sort()) {
        if (dateKey < todayKey) continue;

        const baseEntry = baseline[dateKey];
        const draftEntry = draft[dateKey];

        if (!draftEntry && baseEntry) {
            changedDateCount += 1;
            addDateToTopicMap(removalsByTopicId, baseEntry.topicId, dateKey);
            continue;
        }

        if (draftEntry && (!baseEntry || baseEntry.topicId !== draftEntry.topicId)) {
            changedDateCount += 1;
            if (baseEntry) {
                addDateToTopicMap(removalsByTopicId, baseEntry.topicId, dateKey);
            }
            addDateToTopicMap(additionsByTopicId, draftEntry.topicId, dateKey);
        }
    }

    if (changedDateCount > MAX_SCHEDULE_DRAFT_DATE_CHANGES) {
        throw new Error(
            `Too many date changes in one save (${changedDateCount}). Limit is ${MAX_SCHEDULE_DRAFT_DATE_CHANGES} days — save in smaller batches.`,
        );
    }

    const affectedTopicIds = [
        ...new Set([...removalsByTopicId.keys(), ...additionsByTopicId.keys()]),
    ];
    const failures: ScheduleDraftTopicFailure[] = [];

    for (const topicId of affectedTopicIds) {
        const topic = allTopics.find((t) => t.id === topicId);
        const removeKeys = removalsByTopicId.get(topicId) ?? new Set<string>();
        const addKeys = additionsByTopicId.get(topicId) ?? new Set<string>();
        const involvedDates = [...new Set([...removeKeys, ...addKeys])].sort();

        if (!topic) {
            failures.push({
                topicId,
                message: 'Topic not found in library',
                dateKeys: involvedDates,
            });
            continue;
        }

        try {
            let assignments = await fetchFreshAssignments(topic);

            for (const dateKey of removeKeys) {
                assignments = stripCompanyDateAssignment(assignments, companyId, dateKey);
            }

            for (const dateKey of addKeys) {
                // Idempotent if the topic already had this day (or after a prior strip).
                assignments = stripCompanyDateAssignment(assignments, companyId, dateKey);
                assignments = [
                    ...assignments,
                    {
                        id: uuidv4(),
                        type: topic.type,
                        companyId,
                        date: toIsoLocalMidnightForDateKey(dateKey),
                    },
                ];
            }

            await updateTopicAssignments(topic, assignments);
        } catch (err) {
            const message =
                err instanceof Error && err.message
                    ? err.message
                    : 'Failed to update topic assignments';
            console.error('applyScheduleDraft topic update failed', topicId, err);
            failures.push({
                topicId,
                title: topic.title,
                message,
                dateKeys: involvedDates,
            });
        }
    }

    if (failures.length > 0) {
        throw new ScheduleDraftApplyError(failures);
    }
}

export async function setTodaysTopic(params: {
    companyId: string;
    nextTopic: BriefingTopic;
    allTopics: BriefingTopic[];
    todayKey?: string;
}): Promise<void> {
    const dateKey = params.todayKey ?? getTodayDateKey();
    await setTopicForDate({
        companyId: params.companyId,
        dateKey,
        nextTopic: params.nextTopic,
        allTopics: params.allTopics,
    });
}

export async function clearTodaysTopic(params: {
    companyId: string;
    topic?: BriefingTopic | null;
    allTopics?: BriefingTopic[];
    todayKey?: string;
}): Promise<void> {
    const dateKey = params.todayKey ?? getTodayDateKey();
    await clearTopicForDate({
        companyId: params.companyId,
        dateKey,
        topic: params.topic,
        allTopics: params.allTopics,
    });
}

function normalizeAssignmentEntries(
    assignments: AssignedSafetyLessonEntry[],
    fallbackType: BriefingMediaType,
): Array<{ id: string; type: string; companyId: string; date: string }> {
    return assignments
        .filter((item): item is AssignedSafetyLessonEntry => Boolean(item))
        .map((item) => ({
            id: item.id || uuidv4(),
            type: item.type || fallbackType,
            companyId: item.companyId || '',
            date: item.date || '',
        }))
        .filter((item) => Boolean(item.companyId && item.date));
}

async function fetchFreshAssignments(
    topic: Pick<BriefingTopic, 'id' | 'type'>,
): Promise<AssignedSafetyLessonEntry[]> {
    if (topic.type === 'document') {
        const { data, errors } = await authClient.models.Document.get({ id: topic.id });
        if (errors?.length) {
            throw new Error(errors.map((e) => e.message).filter(Boolean).join('; '));
        }
        return parseAssignedLessons(data?.assignedSafetyLesson);
    }

    const { data, errors } = await authClient.models.Video.get({ id: topic.id });
    if (errors?.length) {
        throw new Error(errors.map((e) => e.message).filter(Boolean).join('; '));
    }
    return parseAssignedLessons(data?.assignedSafetyLesson);
}

async function updateTopicAssignments(
    topic: Pick<BriefingTopic, 'id' | 'type'>,
    assignments: AssignedSafetyLessonEntry[],
): Promise<void> {
    const cleaned = normalizeAssignmentEntries(assignments, topic.type);
    // GraphQL UpdateDocumentInput types assignedSafetyLesson as AWSJSON string.
    const assignedSafetyLessonJson = JSON.stringify(cleaned);
    const query =
        topic.type === 'document' ? updateDocumentAssignmentMutation : updateVideoAssignmentMutation;

    try {
        const response = await authClient.graphql({
            query,
            variables: {
                input: {
                    id: topic.id,
                    assignedSafetyLesson: assignedSafetyLessonJson,
                },
            },
        });

        const payload = response as {
            data?: { updateDocument?: unknown; updateVideo?: unknown } | null;
            errors?: Array<{ message?: string }>;
        };

        if (payload.errors?.length) {
            throw new Error(
                payload.errors.map((entry) => entry.message).filter(Boolean).join('; ') ||
                    'GraphQL assignment update failed',
            );
        }
        return;
    } catch (graphQlError) {
        // Same pattern as TrainingPlan categories/holidays — stringify JSON fields.
        const modelResponse =
            topic.type === 'document'
                ? await authClient.models.Document.update({
                      id: topic.id,
                      assignedSafetyLesson: assignedSafetyLessonJson,
                  })
                : await authClient.models.Video.update({
                      id: topic.id,
                      assignedSafetyLesson: assignedSafetyLessonJson,
                  });

        if (modelResponse.errors?.length) {
            const modelMessage = modelResponse.errors
                .map((entry) => entry.message)
                .filter(Boolean)
                .join('; ');
            const graphQlMessage =
                graphQlError instanceof Error ? graphQlError.message : String(graphQlError);
            throw new Error(modelMessage || graphQlMessage || 'Failed to update topic assignment');
        }
        if (!modelResponse.data) {
            const graphQlMessage =
                graphQlError instanceof Error ? graphQlError.message : String(graphQlError);
            throw new Error(graphQlMessage || 'Failed to update topic assignment');
        }
    }
}

export async function markAsSafetyLesson(topic: Pick<BriefingTopic, 'id' | 'type'>): Promise<void> {
    if (topic.type === 'document') {
        await authClient.models.Document.update({ id: topic.id, safetyLesson: true });
    } else {
        await authClient.models.Video.update({ id: topic.id, safetyLesson: true });
    }
}

export async function unmarkSafetyLesson(topic: Pick<BriefingTopic, 'id' | 'type'>): Promise<void> {
    if (topic.type === 'document') {
        await authClient.models.Document.update({ id: topic.id, safetyLesson: false });
    } else {
        await authClient.models.Video.update({ id: topic.id, safetyLesson: false });
    }
}

export async function updateTopicTitle(
    topic: Pick<BriefingTopic, 'id' | 'type'>,
    title: string,
): Promise<void> {
    if (topic.type === 'document') {
        await authClient.models.Document.update({ id: topic.id, title });
    } else {
        await authClient.models.Video.update({ id: topic.id, title });
    }
}

function inferMediaType(fileName: string): BriefingMediaType {
    const lower = fileName.toLowerCase();
    if (
        lower.endsWith('.mp4') ||
        lower.endsWith('.mov') ||
        lower.endsWith('.webm') ||
        lower.endsWith('.m4v') ||
        lower.endsWith('.avi')
    ) {
        return 'video';
    }
    return 'document';
}

export async function createBriefingTopic(params: {
    companyId: string;
    ownerId: string;
    title: string;
    file: File;
}): Promise<BriefingTopic> {
    const mediaType = inferMediaType(params.file.name);
    const fileName = `${uuidv4()}-${mediaType === 'video' ? 'VIDEO' : 'DOCUMENT'}-${params.file.name}`;
    const contentPath =
        mediaType === 'video'
            ? S3_RESOURCE_LIBRARY_PATH.videoPath(params.ownerId, fileName)
            : S3_RESOURCE_LIBRARY_PATH.documentPath(params.ownerId, fileName);

    await uploadData({
        path: contentPath,
        data: params.file,
    }).result;

    // Match Resource Library create: omit assignedSafetyLesson (AWSJSON). Empty [] can fail Gen2 create.
    const createInput = {
        title: params.title.trim(),
        contentPath,
        sharedWithCompanyIds: [params.companyId],
        safetyLesson: true,
    };

    const response =
        mediaType === 'video'
            ? await authClient.models.Video.create(createInput)
            : await authClient.models.Document.create(createInput);

    if (response.errors?.length) {
        throw new Error(
            response.errors.map((e) => e.message).filter(Boolean).join('; ') ||
                `Failed to create ${mediaType} topic`,
        );
    }

    const created = response.data;
    if (!created?.id || !created.title || !created.contentPath) {
        throw new Error(`Failed to create ${mediaType} topic — no record returned`);
    }

    return toBriefingTopic(
        {
            id: created.id,
            title: created.title,
            contentPath: created.contentPath,
            folderName: created.folderName,
            assignedSafetyLesson: created.assignedSafetyLesson,
        },
        mediaType,
        params.companyId,
    );
}

export function formatLastUsedLabel(lastUsedDate: string | null, todayKey = getTodayDateKey()): string {
    if (!lastUsedDate) return '—';
    if (lastUsedDate === todayKey) return 'Today';
    return formatShortDate(lastUsedDate);
}
